/**
 * Effect sprite pixel data — envelopes, thought bubbles, smoke, sparks, zzz, exclamation.
 *
 * Uses EFFECT_PALETTE from palette.ts:
 *   0 = transparent
 *   1 = white
 *   2 = light grey
 *   3 = mid grey
 *   4 = purple (delegates-to envelope)
 *   5 = cyan (reports-to envelope)
 *   6 = green (peers-with envelope)
 *   7 = pink (pipes-to envelope)
 *   8 = error red
 *   9 = warning yellow
 *  10 = smoke (semi-transparent)
 *  11 = smoke darker
 *  12 = spark yellow
 *  13 = spark orange
 */

import type { SpriteDefinition } from "../SpriteSheet.js";

// Helpers
function px(pixels: number[], w: number, x: number, y: number, c: number): void {
  if (x >= 0 && x < w && y >= 0 && y < (pixels.length / w)) {
    pixels[y * w + x] = c;
  }
}

function rect(pixels: number[], w: number, x: number, y: number, rw: number, rh: number, c: number): void {
  for (let dy = 0; dy < rh; dy++) {
    for (let dx = 0; dx < rw; dx++) {
      px(pixels, w, x + dx, y + dy, c);
    }
  }
}

// ==================================================================
// ENVELOPE — 12x10, colored by edge type
// ==================================================================

function makeEnvelope(bodyColor: number): number[] {
  const W = 12, H = 10;
  const p = new Array<number>(W * H).fill(0);

  // Envelope body
  rect(p, W, 1, 2, 10, 7, bodyColor);
  // White interior visible at top flap
  rect(p, W, 2, 3, 8, 2, 1);
  // Flap (triangle top)
  for (let i = 0; i < 5; i++) {
    px(p, W, 1 + i, 2 - 0, bodyColor);     // left diagonal of flap
    px(p, W, 10 - i, 2 - 0, bodyColor);    // right diagonal
  }
  // Flap fold line
  for (let i = 0; i < 4; i++) {
    px(p, W, 4 + i, 1, bodyColor);
  }
  px(p, W, 5, 0, bodyColor);
  px(p, W, 6, 0, bodyColor);

  // Outline
  for (let x = 0; x < 12; x++) px(p, W, x, 9, 3);
  for (let y = 2; y < 10; y++) { px(p, W, 0, y, 3); px(p, W, 11, y, 3); }

  return p;
}

/** Envelope for delegates-to (purple). */
export const EFFECT_ENVELOPE_PURPLE: SpriteDefinition = {
  name: "envelope-purple",
  frames: [{ width: 12, height: 10, pixels: makeEnvelope(4) }],
  originX: 6, originY: 5,
};

/** Envelope for reports-to (cyan). */
export const EFFECT_ENVELOPE_CYAN: SpriteDefinition = {
  name: "envelope-cyan",
  frames: [{ width: 12, height: 10, pixels: makeEnvelope(5) }],
  originX: 6, originY: 5,
};

/** Envelope for peers-with (green). */
export const EFFECT_ENVELOPE_GREEN: SpriteDefinition = {
  name: "envelope-green",
  frames: [{ width: 12, height: 10, pixels: makeEnvelope(6) }],
  originX: 6, originY: 5,
};

/** Envelope for pipes-to (pink). */
export const EFFECT_ENVELOPE_PINK: SpriteDefinition = {
  name: "envelope-pink",
  frames: [{ width: 12, height: 10, pixels: makeEnvelope(7) }],
  originX: 6, originY: 5,
};

// ==================================================================
// THOUGHT BUBBLE — 16x14, 3 frames
// ==================================================================

function makeThoughtBubbleFrame(phase: number): number[] {
  const W = 16, H = 14;
  const p = new Array<number>(W * H).fill(0);

  // Main bubble
  rect(p, W, 3, 0, 10, 8, 1);
  rect(p, W, 2, 1, 12, 6, 1);
  rect(p, W, 1, 2, 14, 4, 1);
  // Outline
  for (let x = 3; x < 13; x++) { px(p, W, x, 0, 2); }
  for (let x = 1; x < 15; x++) { px(p, W, x, 8, 2); }
  for (let y = 2; y < 6; y++) { px(p, W, 0, y, 2); px(p, W, 15, y, 2); }
  px(p, W, 1, 1, 2); px(p, W, 14, 1, 2);
  px(p, W, 1, 7, 2); px(p, W, 14, 7, 2);

  // Inner "thought" dots — different per frame
  if (phase === 0) {
    px(p, W, 5, 4, 3);
    px(p, W, 8, 4, 3);
    px(p, W, 11, 4, 3);
  } else if (phase === 1) {
    px(p, W, 5, 3, 3);
    px(p, W, 8, 4, 3);
    px(p, W, 11, 5, 3);
  } else {
    px(p, W, 5, 5, 3);
    px(p, W, 8, 3, 3);
    px(p, W, 11, 4, 3);
  }

  // Tail dots (leading to character head)
  px(p, W, 5, 9, 1);
  px(p, W, 4, 10, 1);
  px(p, W, 3, 11, 2);
  px(p, W, 2, 12, 2);

  return p;
}

export const EFFECT_THOUGHT_BUBBLE: SpriteDefinition = {
  name: "thought-bubble",
  frames: [
    { width: 16, height: 14, pixels: makeThoughtBubbleFrame(0) },
    { width: 16, height: 14, pixels: makeThoughtBubbleFrame(1) },
    { width: 16, height: 14, pixels: makeThoughtBubbleFrame(2) },
  ],
  originX: 8, originY: 12,
};

// ==================================================================
// SMOKE PUFF — 12x12, 4 frames (expanding then fading)
// ==================================================================

function makeSmokePuff(size: number): number[] {
  const W = 12, H = 12;
  const p = new Array<number>(W * H).fill(0);
  const cx = 6, cy = 6;

  // Simple circle of given radius
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
      if (dist < size) {
        p[y * W + x] = dist < size * 0.6 ? 10 : 11;
      }
    }
  }

  return p;
}

export const EFFECT_SMOKE: SpriteDefinition = {
  name: "smoke",
  frames: [
    { width: 12, height: 12, pixels: makeSmokePuff(2) },
    { width: 12, height: 12, pixels: makeSmokePuff(3) },
    { width: 12, height: 12, pixels: makeSmokePuff(4) },
    { width: 12, height: 12, pixels: makeSmokePuff(5) },
  ],
  originX: 6, originY: 6,
};

// ==================================================================
// TYPING SPARKS — 10x6, 4 frames
// ==================================================================

function makeSpark(seed: number): number[] {
  const W = 10, H = 6;
  const p = new Array<number>(W * H).fill(0);

  // Scatter a few bright pixels at pseudo-random positions
  const positions = [
    [2, 1], [7, 0], [4, 3], [8, 4], [1, 2], [5, 1], [9, 3], [3, 5],
  ];
  for (let i = 0; i < 4; i++) {
    const idx = (i + seed) % positions.length;
    const [x, y] = positions[idx];
    px(p, W, x, y, i % 2 === 0 ? 12 : 13);
  }

  return p;
}

export const EFFECT_SPARKS: SpriteDefinition = {
  name: "sparks",
  frames: [
    { width: 10, height: 6, pixels: makeSpark(0) },
    { width: 10, height: 6, pixels: makeSpark(2) },
    { width: 10, height: 6, pixels: makeSpark(4) },
    { width: 10, height: 6, pixels: makeSpark(6) },
  ],
  originX: 5, originY: 3,
};

// ==================================================================
// ZZZ — 14x10, 3 frames (floating z's)
// ==================================================================

function makeZzz(phase: number): number[] {
  const W = 14, H = 10;
  const p = new Array<number>(W * H).fill(0);

  // Three Z's at different sizes, offset by phase
  const offsets = [
    { x: 2, y: 6 - phase, size: 3, color: 3 },      // small z
    { x: 6, y: 3 - phase, size: 4, color: 2 },      // medium z
    { x: 9, y: 1 - phase, size: 3, color: 2 },      // small z
  ];

  for (const { x: bx, y: by, size, color } of offsets) {
    if (by < 0 || by + size > H) continue;
    // Top bar
    for (let i = 0; i < size; i++) px(p, W, bx + i, by, color);
    // Diagonal
    for (let i = 0; i < size; i++) px(p, W, bx + size - 1 - i, by + i, color);
    // Bottom bar
    for (let i = 0; i < size; i++) px(p, W, bx + i, by + size - 1, color);
  }

  return p;
}

export const EFFECT_ZZZ: SpriteDefinition = {
  name: "zzz",
  frames: [
    { width: 14, height: 10, pixels: makeZzz(0) },
    { width: 14, height: 10, pixels: makeZzz(1) },
    { width: 14, height: 10, pixels: makeZzz(2) },
  ],
  originX: 7, originY: 8,
};

// ==================================================================
// EXCLAMATION MARK — 6x12
// ==================================================================

function makeExclamation(): number[] {
  const W = 6, H = 12;
  const p = new Array<number>(W * H).fill(0);

  // Stem
  rect(p, W, 2, 0, 2, 8, 8);
  // Dot
  rect(p, W, 2, 10, 2, 2, 8);

  return p;
}

export const EFFECT_EXCLAMATION: SpriteDefinition = {
  name: "exclamation",
  frames: [{ width: 6, height: 12, pixels: makeExclamation() }],
  originX: 3, originY: 10,
};

// ==================================================================
// CHECKMARK — 10x10
// ==================================================================

function makeCheckmark(): number[] {
  const W = 10, H = 10;
  const p = new Array<number>(W * H).fill(0);

  // Simple check shape
  px(p, W, 1, 5, 6);
  px(p, W, 2, 6, 6);
  px(p, W, 3, 7, 6);
  px(p, W, 4, 8, 6);
  px(p, W, 5, 7, 6);
  px(p, W, 6, 6, 6);
  px(p, W, 7, 5, 6);
  px(p, W, 8, 4, 6);
  // Thicker
  px(p, W, 2, 5, 6);
  px(p, W, 3, 6, 6);
  px(p, W, 4, 7, 6);
  px(p, W, 5, 6, 6);
  px(p, W, 6, 5, 6);
  px(p, W, 7, 4, 6);
  px(p, W, 8, 3, 6);

  return p;
}

export const EFFECT_CHECKMARK: SpriteDefinition = {
  name: "checkmark",
  frames: [{ width: 10, height: 10, pixels: makeCheckmark() }],
  originX: 5, originY: 5,
};
